import api from './client';
import { ApplicationStatus } from './applications';
import { OrderStatus } from './orders';

export interface AppUser {
  id: number;
  name: string | null;
  email: string;
  role: 'user';
  isApproved: boolean;
  /** 'google' | 'apple'; null = email & password. */
  provider: string | null;
  stripeCustomerId?: string | null;
  /** Profile photo URL, or null. */
  avatar: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface AppUserRow extends AppUser {
  counts: { applications: number; orders: number; inquiries: number };
}

export interface UserListParams {
  search?: string;
  provider?: 'email' | 'google' | 'apple';
  page?: number;
  limit?: number;
}

export interface UserListResponse {
  total: number;
  page: number;
  limit: number;
  totalPages: number;
  users: AppUserRow[];
}

/** Listing fields included with a user's likes, saves, inquiries and applications. */
export interface UserListingSummary {
  id: number;
  title: string;
  address: string;
  type: 'Rental' | 'Sale';
  price: string | number;
  status: 'active' | 'inactive';
  coverPhoto: string | null;
  photos: string[] | null;
}

export interface UserOrder {
  id: number;
  orderRef: string;
  kind: 'legal' | 'maintenance';
  serviceName: string | null;
  tier: string | null;
  category: string | null;
  amount: string | number | null;
  quotedAmount: string | number | null;
  currency: string;
  paid: boolean;
  status: OrderStatus;
  urgency: 'standard' | 'urgent' | null;
  preferredDate: string | null;
  timeSlot: 'morning' | 'afternoon' | 'evening' | null;
  createdAt: string;
}

export interface UserApplication {
  id: number;
  referenceNo: string;
  listingId: number;
  fullName: string;
  phone: string | null;
  status: ApplicationStatus;
  applicationFee: string | number;
  currency: string;
  feePaid: boolean;
  createdAt: string;
  listing: UserListingSummary | null;
}

export interface UserInquiry {
  id: number;
  status: 'new' | 'contacted' | 'converted';
  message: string | null;
  phone: string | null;
  createdAt: string;
  listing: UserListingSummary | null;
}

export interface UserReview {
  id: number;
  serviceName: string | null;
  providerName: string | null;
  rating: number;
  tags: string[] | null;
  comment: string | null;
  createdAt: string;
}

export interface UserSearchAlert {
  id: number;
  name: string | null;
  criteria: Record<string, unknown> | null;
  isActive: boolean;
  createdAt: string;
}

export interface UserDetail {
  user: AppUser;
  stats: {
    likedListings: number;
    savedListings: number;
    inquiries: number;
    applications: number;
    legalOrders: number;
    maintenanceRequests: number;
    reviews: number;
    searchAlerts: number;
    totalSpent: number;
  };
  likedListings: { likedAt: string; listing: UserListingSummary | null }[];
  savedListings: { savedAt: string; listing: UserListingSummary | null }[];
  inquiries: UserInquiry[];
  applications: UserApplication[];
  orders: UserOrder[];
  reviews: UserReview[];
  searchAlerts: UserSearchAlert[];
}

/** GET /api/users (admin) */
export const listUsers = async (params: UserListParams = {}): Promise<UserListResponse> => {
  const query: Record<string, string> = {};
  if (params.search) query.search = params.search;
  if (params.provider) query.provider = params.provider;
  if (params.page) query.page = String(params.page);
  if (params.limit) query.limit = String(params.limit);
  const res = await api.get<{ status: number } & UserListResponse>('/users', { params: query });
  const { total, page, limit, totalPages, users } = res.data;
  return { total, page, limit, totalPages, users };
};

/** GET /api/users/:id (admin) */
export const getUserDetail = async (id: number): Promise<UserDetail> => {
  const res = await api.get<{ status: number } & UserDetail>(`/users/${id}`);
  const { status, ...detail } = res.data;
  return detail;
};

export const SIGN_IN_LABEL = (provider: string | null): string =>
  provider === 'google' ? 'Google' : provider === 'apple' ? 'Apple' : 'Email';
